import { Link } from 'react-router';
import {
  ChevronLeftIcon,
  MapPinIcon,
  ThumbsUpIcon,
  ClipboardListIcon,
  LogInIcon,
  Loader2Icon,
  CameraIcon
} from 'lucide-react';

import { useGetMyReports } from '@cdf/api-client-react';
import { useSession } from '@/components/auth/user-provider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  getStatusConfig,
  formatRelativeTime,
  SEVERITY_LABELS,
  SEVERITY_COLORS
} from '@/lib/report-helpers';
import { cn } from '@/lib/utils';

export function MyReportsPage() {
  const { session, signIn } = useSession();

  // Signed-out visitors get a sign-in prompt instead of an error.
  if (session.status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (session.status === 'signedOut') {
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-4 px-4 text-center">
        <ClipboardListIcon className="h-12 w-12 text-muted-foreground" />
        <div>
          <h1 className="text-lg font-semibold">My Reports</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sign in with your work account to see reports you've submitted
            or claimed.
          </p>
        </div>
        <Button onClick={signIn}>
          <LogInIcon className="mr-2 h-4 w-4" />
          Sign in with work account
        </Button>
        <Button asChild variant="ghost">
          <Link to="/">
            <ChevronLeftIcon className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
      </div>
    );
  }

  return <MyReportsList />;
}

function MyReportsList() {
  const { data, isLoading, isError } = useGetMyReports();
  const reports = data?.reports ?? [];

  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col bg-background">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Button variant="ghost" size="icon" asChild className="h-8 w-8">
          <Link to="/">
            <ChevronLeftIcon className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-lg font-semibold">My Reports</h1>
      </header>

      <main className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            Couldn't load your reports. Please try again later.
          </div>
        )}

        {!isLoading && !isError && reports.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-12 text-center">
            <ClipboardListIcon className="h-12 w-12 text-muted-foreground" />
            <div>
              <h2 className="text-base font-medium">No reports yet</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Reports you submit while signed in — or claim with a tracking link —
                will appear here so you can track them without keeping the link.
              </p>
            </div>
            <Button asChild>
              <Link to="/report">
                <CameraIcon className="mr-2 h-4 w-4" />
                Report an Issue
              </Link>
            </Button>
          </div>
        )}

        {!isLoading && !isError && reports.length > 0 && (
          <div className="space-y-3">
            {reports.map((report) => {
              const cfg = getStatusConfig(report.status);
              const label = report.customTypeLabel
                ? `Other — ${report.customTypeLabel}`
                : (report.categoryName ?? 'Issue');

              return (
                <Card key={report.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <Link
                      to={`/track/${report.trackingToken}`}
                      className="flex gap-3 p-3 transition-colors hover:bg-muted/50"
                    >
                      {/* Photo thumbnail */}
                      {report.photoUrl ? (
                        <img
                          src={report.photoUrl}
                          alt={label}
                          className="h-16 w-16 shrink-0 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-muted">
                          <CameraIcon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium">{label}</span>
                          <Badge className={cn('shrink-0 text-xs', cfg.badgeClass)}>
                            {cfg.label}
                          </Badge>
                        </div>
                        {report.addressText && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPinIcon className="h-3 w-3" />
                            {report.addressText}
                          </span>
                        )}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {report.severity && SEVERITY_LABELS[report.severity] && (
                            <span className={cn('rounded px-1.5 py-0.5', SEVERITY_COLORS[report.severity])}>
                              {SEVERITY_LABELS[report.severity]}
                            </span>
                          )}
                          {report.upvoteCount > 0 && (
                            <span className="flex items-center gap-1">
                              <ThumbsUpIcon className="h-3 w-3" />
                              {report.upvoteCount}
                            </span>
                          )}
                          <span>{formatRelativeTime(report.updatedAt)}</span>
                        </div>
                      </div>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
